// https://annuities.myaccount.pacificlife.com/enroll-ui/client
import React, { useState, useEffect } from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import ReactTooltip from "react-tooltip";
import Toast from "react-bootstrap/Toast";
import axios from "axios";
import moment from "moment";
import { QuestionCircle } from "react-bootstrap-icons";
import { REQUIRED_FIELDS, POLICYOWNER } from "./constants";
import {
    emailValidation,
    userNameValidation,
    emailVerifyMatch,
    ssnValidation
} from "./validation";
import { analytics } from "./analytics";
import { Header, Footer, DisclaimerPolicyOwner } from "./components";
import DatePicker from "react-datepicker";
import TermsAndConditions from "./Terms&Conditions";
import "react-datepicker/dist/react-datepicker.css";
import { getIPAddress } from "./utils/getIPAddress";

const PolicyOwnerEnroll = () => {
    const [user, setUser] = useState({
        firstName: "",
        lastName: "",
        email: "",
        verifyEmail: "",
        phoneNumber: "",
        verifyPhoneNumber: "",
        secondaryPhoneNumber: "",
        userName: "",
        password: "",
        verifyPassword: "",
        trustName: "",
        tin: "",
        dob: "",
        policyNumber: "",
        middleName: "",
        suffix: "",
        isCheckedFindMyPolicy: false,
        tinType: "",
        tenantRegistrationState: ""
    });
    const [duplicateRegistration, setDuplicateRegistration] = useState(false);
    const [termsAndConditionsOpen, setTermsAndConditionsOpen] = useState(false);
    const [errors, setErrors] = useState({
        firstName: "",
        lastName: "",
        email: "",
        verifyEmail: "",
        phoneNumber: "",
        verifyPhoneNumber: "",
        userName: "",
        password: "",
        verifyPassword: "",
        trustName: "",
        tin: "",
        dob: "",
        policyNumber: "",
        middleName: "",
        suffix: "",
        isCheckedFindMyPolicy: false,
        tinType: ""
    });
    const [toastMessage, setToastMessage] = useState("");
    const [submitDisabled, setSubmitDisabled] = useState(true);
    const [submitting, setsubmitting] = useState(false);

    const setTermsAndConditions = (e) => {
        setTermsAndConditionsOpen(true);
    };

    const closeTermsAndConditions = (value) => {
        setTermsAndConditionsOpen(value);
    };

    useEffect(() => {
        analytics();
    }, []);

    useEffect(() => {
        const role = POLICYOWNER;
        const requiredFields = REQUIRED_FIELDS[role];
        for (const element of requiredFields) {
            const fieldName = element;
            if (!user[fieldName]) {
                setSubmitDisabled(true);
                return;
            }
        }

        const errorFields = Object.keys(errors);
        for (const element of errorFields) {
            const fieldName = element;
            if (errors[fieldName] !== "") {
                setSubmitDisabled(true);
                return;
            }
        }
    }, [user, errors]);

    useEffect(() => {
        document.title = "Policy Owner Registration | Pacific Life";
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        const payload = { [name]: value };
        setUser((prev) => ({ ...prev, ...payload }));
    };

    const handleCheckboxChange = (e) => {
        const name = e.target.name;
        const value = e.target.checked;
        const payload = { [name]: value };
        setUser((prev) => ({ ...prev, ...payload }));
    };

    const handleRadioChange = async (e) => {
        const nameRd = e.target.name;
        const valueRd = e.target.value;
        const payloadRd = { [nameRd]: valueRd };
        await handleBlur(e);
        setUser((prev) => ({ ...prev, ...payloadRd }));
    };

    const handleBlur = async (e) => {
        const { name, value } = e.target;
        let errorMessage = "";

        if (name === "policyNumber") {
            if (value.length === 0) {
                errorMessage = "Policy Number must be entered";
            } else if (/[^a-zA-Z0-9*]/g.test(value)) {
                errorMessage =
                    "Policy Number must not contain spaces or numbers or special characters";
            } else {
                errorMessage = "";
            }
        }
        if (name === "dob") {
            if (value.length === 0) {
                errorMessage = "Insured's Date of Birth must be entered";
            } else {
                errorMessage = "";
            }
        }
        if (name == "trustName") {
            if (user.tinType == "T") {
                if (value) {
                    if (/[^[a-zA-Z0-9\s()&,''-]/.test(value)) {
                        errorMessage =
                            "Trust/Organization entered is not valid";
                    }
                }
            }
        }
        if (name === "tin") {
            const valid = ssnValidation(value);
            errorMessage = valid ? "" : "Enter valid Tax ID";
            if (value === "123456789" && user.tinType === "P") {
                errorMessage = "Enter valid Tax ID";
            }
        }

        if (name === "isCheckedFindMyPolicy") {
            errorMessage = e.target.checked
                ? ""
                : "Agree to the terms and conditions";
        }

        if (name === "tinType") {
            user.tin = "";
            errors.tin = "";
            errorMessage = e.target.value !== "" ? "" : "Select SSN or TIN";
        }

        if (name === "firstName") {
            if (value.length === 0) {
                errorMessage = "First Name must be entered";
            } else if (/[^a-zA-Z-. ]/.test(value)) {
                errorMessage =
                    "First Name must not contain numbers or special characters";
            } else {
                errorMessage = "";
            }
        }

        if (name === "middleName") {
            if (/[^a-zA-Z-. ]/.test(value)) {
                errorMessage =
                    "Middle Name must not contain numbers or special characters";
            } else {
                errorMessage = "";
            }
        }

        if (name === "suffix") {
            if (/[^a-zA-Z-. ]/.test(value)) {
                errorMessage =
                    "Suffix must not contain numbers or special characters";
            } else {
                errorMessage = "";
            }
        }

        if (name === "lastName") {
            if (value.length === 0) {
                errorMessage = "Last Name must be entered";
            } else if (/[^a-zA-Z-. ]/.test(value)) {
                errorMessage =
                    "Last Name must not contain numbers or special characters";
            } else {
                errorMessage = "";
            }
        }

        if (name === "email") {
            const emailValid = emailValidation(value);
            errorMessage = emailValid ? "" : "Enter valid email";
        }

        if (name === "verifyEmail") {
            const emailConfirmValid = emailValidation(value);
            errorMessage = emailConfirmValid
                ? ""
                : "Email addresses do not match";
            if (!errorMessage) {
                errorMessage = emailVerifyMatch(user.email, value)
                    ? ""
                    : "Email addresses do not match";
            }
        }

        if (name === "phoneNumber") {
            errorMessage =
                value.length < 14 ? "Enter a valid 10 digit phone number" : "";
        }
        if (name === "verifyPhoneNumber") {
            errorMessage =
                value.length < 14 ? "Enter a valid 10 digit phone number" : "";
            errorMessage =
                user.phoneNumber !== value
                    ? "Phone number do not match"
                    : errorMessage;
        }
        if (name === "password") {
            errorMessage =
                value.length < 6
                    ? "Enter a valid password above 6 characters"
                    : "";
        }
        if (name === "confirmPassword") {
            errorMessage =
                user.password !== value
                    ? "Password do not match"
                    : errorMessage;
        }
        if (name === "id") {
            const validUserName = userNameValidation(value);
            const tooShortOrLongUserName =
                value.length < 6 || value.length > 15;
            if (value.indexOf(" ") > -1) {
                errorMessage = "Username name should not contain space";
            }
            if (/[^a-zA-Z0-9-. ]/.test(value)) {
                errorMessage = "Username must not contain special characters";
            }
            if (validUserName) {
                errorMessage = "";
            } else if (tooShortOrLongUserName) {
                errorMessage =
                    "Username must be between 6 and 15 characters long";
            }
        }
        if (name === "dob") {
            const validDate = validateDate(value);

            if (validDate) {
                errorMessage = "";
            } else {
                errorMessage = "Enter valid date of birth";
            }
        }
        if (errorMessage != "") {
            setSubmitDisabled(true);
        } else {
            setSubmitDisabled(false);
        }
        setErrors((prev) => ({ ...prev, [name]: errorMessage }));
    };

    const validateDate = (dateString) => {
        const date = new Date(dateString);
        return !isNaN(date.getTime()); // Check if the date is valid
    };

    const formatPayloadFindMyPolicy = () => {
        return {
            policyNumber: user.policyNumber,
            ssnTIN: user.tin,
            dateOfBirth: moment(user.dob).format("YYYY-MM-DD"),
            tinType: user.tinType
        };
    };

    const authorize = async () => {
        const authorizeUrl = `${decodeURIComponent(
            user.tenantRegistrationState
        )}&redirect_uri=https://${
            document.location.hostname
        }/enroll-ui/potentialinsured`;
        window.location.href = authorizeUrl;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        let errMsg = "";
        //const test = JSON.stringify(payload);
        setsubmitting(true);

        const payload = await formatPayloadProfile();
        try {
            const res = await axios({
                method: "POST",
                url: "/life/enroll/api/userenroll/client/enroll/",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json"
                },
                data: JSON.stringify(payload)
            }).catch(function (err) {
                if (err.response) {
                    errMsg = err.response?.data?.error?.message;
                    console.log(
                        err.response?.data?.error?.message ||
                            err.response?.data ||
                            "Something went wrong.  Please try again later."
                    );
                    setToastMessage(
                        errMsg ||
                            "Something went wrong. Please try again later."
                    );
                } else if (err.request) {
                    console.log(err.request);
                } else {
                    console.log("Error", err.message);
                }
            });
            if (res.status === 200) {
                if (res.data.state) {
                    setDuplicateRegistration(true);
                    user.tenantRegistrationState = res.data.state;
                } else {
                    window.location.href =
                        "/policy-owner-login/account/reg-successful.html";
                }
            }
        } catch (error) {
            setToastMessage(
                errMsg ||
                    error.response?.data?.errorMessage ||
                    "Something went wrong. Please try again later."
            );
        }
        setsubmitting(false);
    };

    const formatPayloadProfile = async () => {
        return {
            id: "",
            firstName: user.firstName,
            lastName: user.lastName,
            ssnTin: user.tin,
            secondaryPhone: user.secondaryPhoneNumber,
            specialAccessXML: "",
            createdBy: user.id,
            email: user.email.trim(),
            phoneNumber: user.phoneNumber,
            userId: user.id,
            trustName: user.trustName,
            invitationCode: "",
            role: "client",
            middleName: user.middleName,
            tinType: user.tinType,
            password: user.password,
            policyNumber: user.policyNumber,
            ipAddress: await getIPAddress(),
            suffix: user.suffix,
            dateOfBirth: moment(user.dob).format("YYYY-MM-DD"),
            tenant: "MLA"
        };
    };

    const closeToast = () => {
        setToastMessage("");
    };

    return (
        <>
            {toastMessage !== "" && (
                <Toast onClose={closeToast} className="toast-cont">
                    <Toast.Header>
                        <strong className="me-auto">
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: toastMessage
                                }}
                            ></div>
                        </strong>
                    </Toast.Header>
                </Toast>
            )}
            <Header />
            <div className="mainMyAccount">
                <h1 className="heading">Register</h1>
                <Form>
                    <Row>
                        <Col sm={12}>
                            <label className="sub-title">
                                Policy Information
                            </label>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={3}>
                            <input
                                type="radio"
                                name="tinType"
                                value="P"
                                onChange={handleRadioChange}
                                checked={user.tinType === "P"}
                            />
                            &nbsp;&nbsp; SSN
                        </Col>
                        <Col md={6}>
                            <input
                                type="radio"
                                name="tinType"
                                value="T"
                                onChange={handleRadioChange}
                                checked={user.tinType === "T"}
                            />
                            &nbsp;&nbsp; TIN/EIN
                            <QuestionCircle
                                tabIndex="-1"
                                className="iconClass"
                                data-tip="The TIN/EIN is the tax id for a trust or organization."
                            />
                            <span className="validation-error-message">
                                {errors.tinType}
                            </span>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={4}>
                            <Form.Label>
                                Policyowner's Tax ID
                                <QuestionCircle
                                    tabIndex="-1"
                                    className="iconClass"
                                    data-tip="If the policyowner does not have a Social Security Number (SSN) or Tax Identification Number (TIN), please contact Customer Service: (800) 347-7787."
                                />
                            </Form.Label>
                            <Form.Control
                                name="tin"
                                autoComplete="off"
                                maxLength={9}
                                value={user.tin}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                            <span className="validation-error-message">
                                {errors.tin}
                            </span>
                        </Col>

                        <Col md={4}>
                            <Form.Label>
                                Policy Number
                                <QuestionCircle
                                    tabIndex="-1"
                                    className="iconClass"
                                    data-tip="Enter a policy number associated to the provided Tax ID (do not include dashes)."
                                />
                            </Form.Label>
                            <Form.Control
                                name="policyNumber"
                                autoComplete="off"
                                maxLength={10}
                                value={user.policyNumber}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                            <span className="validation-error-message">
                                {errors.policyNumber}
                            </span>
                        </Col>
                        <Col md={4}>
                            <ReactTooltip />
                            <Form.Label>Insured's Date of Birth</Form.Label>
                            <DatePicker
                                selected={user.dob}
                                value={user.dob}
                                className="react-datepicker-ignore-onclickoutside"
                                onChange={(dob) =>
                                    handleChange({
                                        target: {
                                            name: "dob",
                                            value: dob
                                        }
                                    })
                                }
                                showYearDropdown
                                showMonthDropdown
                                dropdownMode="select"
                                placeholderText="Click to select a date"
                                maxDate={new Date()}
                                name="dob"
                                //openToDate={new Date("1985/01/01")}
                                //tabIndex={1}
                                onBlur={handleBlur}
                            />
                            <span className="validation-error-message">
                                <br />
                                {errors.dob}
                            </span>
                        </Col>
                    </Row>
                    {user.tinType == "T" && (
                        <Row>
                            <Col md={4}>
                                <Form.Label>
                                    Trust/Organization (Optional)
                                </Form.Label>
                                <Form.Control
                                    name="trustName"
                                    autoComplete="off"
                                    maxLength={30}
                                    value={user.trustName}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                />
                                <span className="validation-error-message">
                                    {errors.trustName}
                                </span>
                            </Col>
                        </Row>
                    )}

                    <Row>
                        <Col sm={12}>
                            <label className="sub-title">
                                Contact Information
                            </label>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <Form.Label>First Name</Form.Label>
                            <Form.Control
                                name="firstName"
                                autoComplete="off"
                                maxLength={25}
                                value={user.firstName}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                            <span className="validation-error-message">
                                {errors.firstName}
                            </span>
                        </Col>
                        <Col md={6}>
                            <Form.Label>Last Name</Form.Label>
                            <Form.Control
                                name="lastName"
                                autoComplete="off"
                                maxLength={35}
                                value={user.lastName}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                            <span className="validation-error-message">
                                {errors.lastName}
                            </span>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <Form.Label>Middle Name (Optional)</Form.Label>
                            <Form.Control
                                name="middleName"
                                autoComplete="off"
                                maxLength={25}
                                value={user.middleName}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                            <span className="validation-error-message">
                                {errors.middleName}
                            </span>
                        </Col>
                        <Col md={6}>
                            <Form.Label>Suffix (Optional)</Form.Label>
                            <Form.Control
                                name="suffix"
                                autoComplete="off"
                                maxLength={35}
                                value={user.suffix}
                                onChange={handleChange}
                            />
                            <span className="validation-error-message">
                                {errors.suffix}
                            </span>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <ReactTooltip />
                            <Form.Label className="emailClass">
                                Email
                                <QuestionCircle
                                    tabIndex="-1"
                                    className="iconClass"
                                    data-tip="Enter an email address to be associated with your account."
                                />
                            </Form.Label>
                            <Form.Control
                                name="email"
                                autoComplete="off"
                                maxLength={60}
                                value={user.email}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                            <span className="validation-error-message">
                                {errors.email}
                            </span>
                        </Col>
                        <Col md={6}>
                            <Form.Label>Confirm Email</Form.Label>
                            <Form.Control
                                name="verifyEmail"
                                autoComplete="off"
                                maxLength={60}
                                value={user.verifyEmail}
                                onChange={handleChange}
                                onPaste={(e) => e.preventDefault()}
                                onBlur={handleBlur}
                            />
                            <span className="validation-error-message">
                                {errors.verifyEmail}
                            </span>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <ReactTooltip />
                            <Form.Label className="createUsername">
                                Username
                                <QuestionCircle
                                    tabIndex="-1"
                                    className="iconClass"
                                    data-tip="Remember this! You will need it along with your password to access your account information."
                                />
                            </Form.Label>
                            <Form.Control
                                name="id"
                                autoComplete="off"
                                maxLength={20}
                                value={user.id}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                            <span className="validation-error-message">
                                {errors.id}
                            </span>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={12}>
                            <input
                                type="checkbox"
                                name="isCheckedFindMyPolicy"
                                checked={user.isCheckedFindMyPolicy}
                                onChange={handleCheckboxChange}
                                onClick={handleBlur}
                            />
                            &nbsp;&nbsp;By clicking on "Register", you agree to
                            the{" "}
                            <a href="#" onClick={setTermsAndConditions}>
                                {" "}
                                Terms and Conditions
                            </a>{" "}
                            of this site.
                            <br />
                            <TermsAndConditions
                                TermsAndConditionsOpen={termsAndConditionsOpen}
                                CloseTermsAndConditions={
                                    closeTermsAndConditions
                                }
                            ></TermsAndConditions>
                            <span className="validation-error-message">
                                {errors.isCheckedFindMyPolicy}
                            </span>
                        </Col>
                    </Row>

                    <div className="buttons-myaccount">
                        <Button
                            variant="primary"
                            type="submit"
                            onClick={handleSubmit}
                            data-track="analytics"
                            disabled={submitDisabled || submitting}
                        >
                            {submitting ? "Registering" : "Register"}
                        </Button>
                    </div>
                </Form>

                <DisclaimerPolicyOwner />
            </div>
            <Footer />
        </>
    );
};

export default PolicyOwnerEnroll;
